import { Fragment } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useAuth, useClerk } from "@clerk/clerk-react";
import { useQueryClient } from "@tanstack/react-query";
import { usePostHog } from "posthog-js/react";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
} from "@heroicons/react/24/solid";
import { LifeBuoyIcon } from "lucide-react";
import { useSelector } from "react-redux";
import { authSelectors } from "../../store/auth/selector";
import { actions } from "../../store/auth/slice";
import { Avatar } from "../account/Avatar";
import Logo from "../Logo";

export function SellerNavbar() {
  const account = useSelector(authSelectors.account);
  const dispatch = useDispatch();
  const history = useHistory();
  const { signOut } = useAuth();
  const { openUserProfile } = useClerk();
  const queryClient = useQueryClient();
  const posthog = usePostHog();

  return (
    <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-6 flex-shrink-0">
      <Logo mode="light" width={74} height={24} />

      {account && (
        <Popover className="relative z-30">
          {({ close }) => (
            <>
              <PopoverButton className="flex items-center rounded-full focus:outline-none focus:ring-2 focus:ring-persian-400 focus:ring-offset-2">
                <Avatar size="l" account={account} withBorder={false} />
              </PopoverButton>

              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <PopoverPanel className="absolute right-0 mt-2 w-64 rounded-lg bg-white shadow-lg ring-1 ring-black/5">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-semibold text-gray-900">{account.name}</p>
                    <p className="text-xs text-gray-500 truncate">{account.email}</p>
                  </div>
                  <div className="p-1">
                    <button
                      onClick={() => { close(); openUserProfile(); }}
                      className="w-full text-left px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-x-3 transition-colors"
                    >
                      <Cog6ToothIcon className="w-4 h-4 text-gray-500" />
                      Account Settings
                    </button>
                    <button
                      onClick={() => { close(); (window as any).Plain?.open(); }}
                      className="w-full text-left px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 rounded-md flex items-center gap-x-3 transition-colors"
                    >
                      <LifeBuoyIcon className="w-4 h-4 text-gray-500" />
                      Help & Support
                    </button>
                    <div className="border-t border-gray-100 my-1" />
                    <button
                      onClick={async () => {
                        await signOut();
                        posthog?.reset();
                        dispatch(actions.logout());
                        queryClient.removeQueries();
                        history.push("/login");
                      }}
                      className="w-full text-left px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-md flex items-center gap-x-3 transition-colors"
                    >
                      <ArrowLeftOnRectangleIcon className="w-4 h-4 text-red-500" />
                      Logout
                    </button>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      )}
    </header>
  );
}
